import { useState } from 'react';
import { useTranslate } from 'react-admin';
import { Alert, Stack, Typography } from '@mui/material';

import {
  approveClosureResolution,
  cancelClosureResolution,
  proposeClosureResolution,
  type ClosureItem,
  type ClosurePath,
  type ClosurePreview,
  type ClosureProposal,
  type QueueList,
} from 'src/pages/admin/services/vaultOperationsService';

import { ConfirmDialog, type ConfirmSpec } from './ConfirmDialog';
import { OPERATIONS_NS as NS } from './constants';
import {
  adminName,
  bookingFigures,
  closedFractionOf,
  fractionToPercent,
  middle,
  percentToFraction,
  durationText,
  minutesUntil,
  money2,
  money6,
  sumLine,
  requiredText,
  sameDecimal,
  validateRecovered,
} from './helpers';
import {
  ActionButton,
  EmptyState,
  ItemCard,
  ListNotices,
  nodes,
  CopyButton,
  ProgressStrip,
  TabIntro,
  Term,
  TimeText,
  useNow,
} from './parts';
import type { Queue } from './useQueue';

interface Props {
  queue: Queue<QueueList<ClosureItem>>;
  vaultName: (vaultId: string | null | undefined) => string;
  onSettled: () => Promise<unknown>;
}

/** The closed fraction in the unit the operator types: "98.5 %". */
const percentText = (fraction: string | null | undefined) =>
  fraction == null ? '—' : `${fractionToPercent(fraction)} %`;

/** Holder: <name> (<short id>) when the BFF serves a name, else the short id, with copy (rule 4). */
const HolderLine = ({
  item,
  t,
}: {
  item: ClosureItem;
  t: (key: string, options?: object) => string;
}) => {
  const short = middle(item.holderId, 8, 4);
  return (
    <Typography variant="body2" component="div" data-testid="holder-line">
      {item.holderName
        ? t('holderNamed', { name: item.holderName, holder: short })
        : t('holder', { holder: short })}
      <CopyButton value={item.holderId} />
    </Typography>
  );
};

/** "Escalated redemptions": the quant's close-out could not finish; settle or release it before the 4 h SLA. */
export const ClosuresTab = ({ queue, vaultName, onSettled }: Props) => {
  const translate = useTranslate();
  const t = (key: string, options?: object) => translate(`${NS}.closures.${key}`, options);
  const c = (key: string, options?: object) => translate(`${NS}.confirm.${key}`, options);
  const [spec, setSpec] = useState<ConfirmSpec | null>(null);
  const now = useNow();

  const pathName = (path: ClosurePath) => t(`path.${path}`);

  /** What the engine would book, as the confirmation lists it (6 dp), or the sentence that it was not previewed. */
  const slippageLabel = <Term term="slippage">{translate(`${NS}.closures.slippage`)}</Term>;

  const bookingText = (preview: ClosurePreview | null | undefined, path: ClosurePath) => {
    // Release writes no journal entry (engine closure_resolution.go: "the recovered USDC stays in V"): nothing is paid.
    if (path === 'RELEASE') {
      return (
        <>
          {t('money.releases')}
          {preview?.slippage != null &&
            nodes(
              (v) =>
                ` ${t('money.releasesSlippage', { slippage: money6(preview.slippage), ...v })}`,
              {
                slippageLabel,
              }
            )}
        </>
      );
    }
    const figures = bookingFigures(preview);
    if (!figures) return t('money.notPreviewed', { path: pathName(path) });
    return (
      <>
        {t('money.books', { path: pathName(path), ...figures })}
        {preview?.slippage != null &&
          nodes(
            (v) => ` ${t('money.booksSlippage', { slippage: money6(preview.slippage), ...v })}`,
            {
              slippageLabel,
            }
          )}
      </>
    );
  };

  /** The engine previews a path on the quant's own figures only; figures typed by the operator are not previewed. */
  const proposeMoney =
    (item: ClosureItem, path: ClosurePath, preview: ClosurePreview) =>
    (values: Record<string, string>) => {
      const onQuantFigures =
        sameDecimal(values.recovered, item.quantRecovered) &&
        sameDecimal(closedFractionOf(values), item.quantClosedFraction);
      return (
        <>
          {t('money.proposeNothingYet')}{' '}
          {onQuantFigures && preview.refusal
            ? t('money.refusedOnQuant', { path: pathName(path), reason: preview.refusal })
            : path === 'RELEASE'
              ? bookingText(onQuantFigures ? preview : null, path)
              : !onQuantFigures
                ? t('money.notPreviewed', { path: pathName(path) })
                : bookingText(preview, path)}
        </>
      );
    };

  const withdraw = (item: ClosureItem, proposal: ClosureProposal) =>
    setSpec({
      title: t('withdrawTitle'),
      summary: <ProgressStrip proposal={proposal} youProposed={proposal.youProposed} />,
      moneyMoves: false,
      money: c('withdrawNoMoney'),
      approver: c('noApproval'),
      ifNobody: t('withdrawIfNobody'),
      confirmLabel: t('withdraw'),
      fields: [],
      submit: () => cancelClosureResolution(item.instructionId, proposal.id),
      doneMessage: t('done.cancel'),
    });

  const approve = (item: ClosureItem, proposal: ClosureProposal) =>
    setSpec({
      title: t('approveTitle', { path: pathName(proposal.path) }),
      summary: (
        <Stack spacing={0.5}>
          <ProgressStrip proposal={proposal} youProposed={false} />
          <HolderLine item={item} t={t} />
          <Typography variant="body2">
            {c('proposerNote', { name: adminName(proposal.proposedByAdmin, proposal.proposedBy) })}{' '}
            “{proposal.note}”
          </Typography>
          <Typography variant="body2">
            {c('evidence')}: {proposal.evidenceRef}
          </Typography>
          <Typography variant="body2">
            {t('figures', {
              recovered: money6(proposal.recovered),
              percent: percentText(proposal.closedFraction),
            })}
          </Typography>
        </Stack>
      ),
      moneyMoves: true,
      // (an approval whose preview carries a refusal is disabled on the card: this dialog never opens for it)
      money: bookingText(item.nextStep.booking, proposal.path),
      approver: c('approverFinal'),
      ifNobody: t('approveIfNobody'),
      stepUpReason: t('stepUp'),
      confirmLabel: t('approve', { path: pathName(proposal.path) }),
      fields: [],
      submit: () => approveClosureResolution(item.instructionId, proposal.id),
      doneMessage: t('done.approve'),
    });

  const propose = (item: ClosureItem, path: ClosurePath) => {
    const preview = path === 'SETTLE' ? item.settle : item.release;
    setSpec({
      title: t('proposeTitle', { path: pathName(path), vault: vaultName(item.vaultId) }),
      summary: (
        <Stack spacing={1}>
          <HolderLine item={item} t={t} />
          <Typography variant="body2">
            {t('quantFigures', {
              recovered: money6(item.quantRecovered),
              percent: percentText(item.quantClosedFraction),
            })}
          </Typography>
          {preview.refusal && (
            <Alert severity="info">
              {t('refused', { path: pathName(path), reason: preview.refusal })}
            </Alert>
          )}
        </Stack>
      ),
      moneyMoves: false,
      // Nothing moves on the proposal itself; the sentence says what the approval books and follows the figures typed.
      money: proposeMoney(item, path, preview),
      // On the quant's own figures a refused path would make a proposal nobody can approve: the venue's figures must differ.
      blocked: (v) =>
        preview.refusal &&
        sameDecimal(v.recovered, item.quantRecovered) &&
        sameDecimal(closedFractionOf(v), item.quantClosedFraction)
          ? t('refusedBlock', { path: pathName(path), reason: preview.refusal })
          : undefined,
      approver: c('approverNext'),
      ifNobody: t('proposeIfNobody'),
      confirmLabel: t('propose', { path: pathName(path) }),
      fields: [
        {
          name: 'recovered',
          label: t('recovered'),
          hint: t('recoveredHint'),
          initial: item.quantRecovered ?? '',
          validate: validateRecovered,
        },
        {
          name: 'closeMode',
          label: t('closeMode'),
          hint: t('closeModeHint'),
          initial: sameDecimal(item.quantClosedFraction, '1') ? 'FULL' : 'PART',
          options: [
            { value: 'FULL', label: t('closeFull') },
            { value: 'PART', label: t('closePart') },
          ],
          // Settle needs everything closed (a Part closed of 100 % is the same 1); Release needs less than 1.
          validate: (mode, v) =>
            path === 'SETTLE' && closedFractionOf(v) !== '1'
              ? `${NS}.errors.settleFraction`
              : path === 'RELEASE' &&
                  !item.ledgerAdjusted &&
                  (mode === 'FULL' || closedFractionOf(v) === '1')
                ? `${NS}.errors.releaseFraction`
                : undefined,
        },
        {
          name: 'closedPercent',
          label: t('closedPercent'),
          hint: t('closedPercentHint'),
          initial: sameDecimal(item.quantClosedFraction, '1')
            ? ''
            : fractionToPercent(item.quantClosedFraction),
          visible: (v) => v.closeMode === 'PART',
          // 0 is allowed: the venue closed nothing and the BFF accepts closedFraction 0
          validate: (p) =>
            percentToFraction(p) === null ? `${NS}.errors.percentFormat` : undefined,
        },
        {
          name: 'evidenceRef',
          label: c('evidenceRef'),
          hint: t('evidenceHint'),
          validate: requiredText,
        },
        {
          name: 'note',
          label: c('note'),
          hint: t('noteHint'),
          multiline: true,
          validate: requiredText,
        },
      ],
      submit: (v, requestId) =>
        proposeClosureResolution(item.instructionId, {
          path,
          recovered: v.recovered,
          closedFraction: closedFractionOf(v) as string,
          // The venue's figures differ from the quant's last report: the BFF requires saying so.
          overrideQuantFigures:
            !sameDecimal(v.recovered, item.quantRecovered) ||
            !sameDecimal(closedFractionOf(v), item.quantClosedFraction),
          evidenceRef: v.evidenceRef,
          note: v.note,
          requestId,
        }),
      doneMessage: t('done.propose'),
    });
  };

  const items = queue.data?.items ?? [];
  return (
    <Stack spacing={2}>
      <TabIntro tab="closures" />
      {queue.data && (
        <ListNotices
          consistent={queue.data.consistent}
          truncated={queue.data.truncated}
          shown={items.length}
          onRefresh={queue.refetch}
        />
      )}
      {queue.data && items.length === 0 && (
        <EmptyState tab="closures" checkedAt={queue.data.checkedAt} />
      )}
      {items.map((item) => {
        const proposal = item.resolution;
        const kind = item.nextStep.kind;
        const left = minutesUntil(item.slaDueAt, now);
        const overdue = item.slaOverdue || (left !== null && left < 0);
        return (
          <ItemCard
            key={item.instructionId}
            testId={`closure-${item.instructionId}`}
            title={t('title', { vault: vaultName(item.vaultId) })}
            status={item.uxStatus}
            whatHappened={translate(`${NS}.whatHappened.closures`)}
            affected={
              <>
                {t('affected', {
                  vault: vaultName(item.vaultId),
                  requested: money2(item.requested),
                  claim: money2(item.claim),
                  recovered: money2(item.quantRecovered),
                })}
                {item.ledgerAdjusted && ` ${t('ledgerAdjusted')}`}
              </>
            }
            extra={
              proposal && (
                <Stack spacing={0.5}>
                  <ProgressStrip proposal={proposal} youProposed={proposal.youProposed} />
                  <Typography variant="body2">
                    {t('proposedAs', { path: pathName(proposal.path) })} “{proposal.note}”
                  </Typography>
                </Stack>
              )
            }
            deadline={
              <Typography
                variant="body2"
                component="span"
                data-testid="sla"
                sx={{
                  color: overdue ? 'error.main' : undefined,
                  fontWeight: overdue ? 600 : undefined,
                }}
              >
                {left === null
                  ? '—'
                  : overdue
                    ? t('slaOverdue', { time: durationText(left, translate) })
                    : t('slaDue', { time: durationText(left, translate) })}{' '}
                (<TimeText iso={item.slaDueAt} />)
              </Typography>
            }
            actions={
              <>
                {kind === 'PROPOSE_RESOLUTION' && (
                  <>
                    <Stack spacing={0.5} sx={{ maxWidth: 360 }}>
                      <ActionButton
                        needs="propose"
                        variant="contained"
                        disabled={item.ledgerAdjusted}
                        onClick={() => propose(item, 'SETTLE')}
                      >
                        {item.settle.net != null || item.settle.paid != null
                          ? t('settle', { paid: money2(item.settle.net ?? item.settle.paid) })
                          : pathName('SETTLE')}
                      </ActionButton>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary' }}
                        data-testid="settle-help"
                      >
                        {item.ledgerAdjusted
                          ? t('settleHelpLedger')
                          : item.settle.net != null &&
                              item.settle.paid != null &&
                              item.settle.fee != null
                            ? (([paid, fee, net]) => t('settleHelp', { paid, fee, net }))(
                                sumLine(item.settle.paid, item.settle.fee, item.settle.net)
                              )
                            : t('settleHelpPlain')}
                      </Typography>
                    </Stack>
                    <Stack spacing={0.5} sx={{ maxWidth: 360 }}>
                      <ActionButton
                        needs="propose"
                        variant="outlined"
                        onClick={() => propose(item, 'RELEASE')}
                      >
                        {t('release')}
                      </ActionButton>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary' }}
                        data-testid="release-help"
                      >
                        {t(item.ledgerAdjusted ? 'releaseHelpLedger' : 'releaseHelp')}
                      </Typography>
                    </Stack>
                  </>
                )}
                {kind === 'WAIT_FOR_SECOND_APPROVER' && proposal && (
                  <>
                    <ActionButton
                      needs="propose"
                      variant="outlined"
                      onClick={() => withdraw(item, proposal)}
                    >
                      {t('withdraw')}
                    </ActionButton>
                  </>
                )}
                {kind === 'APPROVE_RESOLUTION' && proposal && (
                  <>
                    <ActionButton
                      needs="approve"
                      variant="contained"
                      disabled={Boolean(item.nextStep.booking?.refusal)}
                      onClick={() => approve(item, proposal)}
                    >
                      {t('approve', { path: pathName(proposal.path) })}
                    </ActionButton>
                    {item.nextStep.booking?.refusal && (
                      <Typography
                        variant="body2"
                        data-testid="approve-refused"
                        sx={{ alignSelf: 'center', color: 'warning.main' }}
                      >
                        {t('approveRefused', {
                          path: pathName(proposal.path),
                          reason: item.nextStep.booking.refusal,
                        })}
                      </Typography>
                    )}
                    <ActionButton
                      needs="propose"
                      variant="outlined"
                      onClick={() => withdraw(item, proposal)}
                    >
                      {t('reject')}
                    </ActionButton>
                  </>
                )}
              </>
            }
            technical={{
              instructionId: item.instructionId,
              holderId: item.holderId,
              status: item.status,
              ledgerAdjusted: item.ledgerAdjusted,
              ...item.technical,
              proposalId: proposal?.id,
              source: proposal?.source,
              settleRefusal: item.settle.refusal,
              releaseRefusal: item.release.refusal,
            }}
          />
        );
      })}
      {spec && <ConfirmDialog spec={spec} onClose={() => setSpec(null)} onSettled={onSettled} />}
    </Stack>
  );
};
